import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

export default function AccelerationCalculator() {
  const [initialVelocity, setInitialVelocity] = useState("");
  const [finalVelocity, setFinalVelocity] = useState("");
  const [time, setTime] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculateAcceleration = () => {
    const vi = parseFloat(initialVelocity);
    const vf = parseFloat(finalVelocity);
    const t = parseFloat(time);

    if (isNaN(vi) || isNaN(vf) || isNaN(t) || t === 0) {
      toast.error("Please enter valid values (time must be non-zero)");
      return;
    }

    const acceleration = (vf - vi) / t;
    setResult(acceleration);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setInitialVelocity("");
    setFinalVelocity("");
    setTime("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Acceleration Calculator" 
      calculatorPath="/calculator/acceleration"
      category="physics"
      subcategory="Mechanics"
    >
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Acceleration Calculator</h1>
          <p className="text-muted-foreground">
            Calculate acceleration from change in velocity
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: a = Δv / Δt</CardTitle>
            <CardDescription>
              Where a is acceleration (m/s²), Δv is change in velocity (m/s), and Δt is time (s)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="initialVelocity">Initial Velocity (m/s)</Label>
                <Input
                  id="initialVelocity"
                  type="number"
                  step="0.01"
                  placeholder="Enter initial velocity"
                  value={initialVelocity}
                  onChange={(e) => setInitialVelocity(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="finalVelocity">Final Velocity (m/s)</Label>
                <Input
                  id="finalVelocity"
                  type="number"
                  step="0.01"
                  placeholder="Enter final velocity"
                  value={finalVelocity}
                  onChange={(e) => setFinalVelocity(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Time (s)</Label>
                <Input
                  id="time"
                  type="number"
                  step="0.01"
                  placeholder="Enter time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateAcceleration} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && (
              <>
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(2)} m/s²
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Acceleration
                    </p>
                  </CardContent>
                </Card>

                <CalculatorSuperCalcCTA config={calculatorCTAs["acceleration"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>1. Enter the initial velocity in meters per second</p>
                <p>2. Enter the final velocity in meters per second</p>
                <p>3. Enter the time interval in seconds</p>
                <p>4. Click Calculate to get acceleration</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 0 to 20 m/s in 4 s = 5 m/s² acceleration
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
